import React, { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, TriangleAlert } from 'lucide-react';
import { Card, ScrollFade } from '@app/components/core';

export interface ListRow {
  id: string;
  name: string;
  /**
   * What tells this row apart from another with the same name, set beside it as part of the label —
   * not a badge. A badge reads as a property of the thing named; this is the rest of the naming.
   */
  detail?: string;
  /** A figure about the row, on the right before the chevron — build it with `RowBadge`. */
  badge?: ReactNode;
  /** Detail too long for the row, surfaced on hover. */
  hint?: string;
}

/**
 * The figure a row carries on its right, for something the reader is expected to act on. Kept here
 * beside the row markup rather than written per card, because these rows sit next to each other and a
 * badge differing by a pixel of padding is what makes a row look like a different size.
 */
export const RowBadge = ({ children }: { children: ReactNode }): ReactElement => (
  <span className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium tabular-nums text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
    {children}
  </span>
);

/**
 * The shape the Products row of Insights is built from: a titled card holding a list of rows that each
 * open something.
 *
 * Every row is one line of the same height, whatever the card — these are read across, as one row of the
 * page, and a taller row in one of them reads as a different kind of thing rather than as the same thing
 * with more to say. Anything that doesn't fit on the line goes in the badge or the hint, not onto a
 * second line.
 *
 * **A failed read is never an empty list.** This card is the replacement for one that swallowed a refusal
 * and announced "all approved" while closed sprints piled up behind it, so `failed` has its own state and
 * says plainly that it is not claiming there is nothing.
 *
 * Generic over the row so a caller can hang whatever the click needs on it (a sprint as well as a
 * product, say) and still read it back typed in `onOpen`.
 */
export const ListCard = <T extends ListRow>({
  icon: Icon,
  title,
  meta,
  rows,
  empty,
  failed,
  onOpen,
}: {
  icon: React.ElementType;
  title: string;
  /** Aside to the title, top right — a total or a window the list is bounded by. */
  meta?: ReactNode;
  rows: T[];
  empty: string;
  /** Whether the read behind this card failed. Takes precedence over an empty list. */
  failed?: boolean;
  onOpen: (row: T) => void;
}): ReactElement => {
  const { t } = useTranslation();

  const body = (): ReactElement => {
    if (failed) {
      return (
        <p className="flex min-h-40 flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-destructive/40 px-3 text-center text-sm text-muted-foreground">
          <TriangleAlert className="size-5 text-destructive/70" aria-hidden />
          {t('cardReadFailed')}
        </p>
      );
    }

    if (!rows.length) {
      return (
        <p className="flex min-h-40 flex-1 items-center justify-center rounded-xl border border-dashed border-border px-3 text-center text-sm text-muted-foreground">
          {empty}
        </p>
      );
    }

    return (
      // A bounded, scrolling list rather than one that stretches: these cards sit in a row and a long
      // backlog in one of them shouldn't stretch the one beside it. The fade says there's more below.
      <ScrollFade
        wrapperClassName="min-h-0 flex-1"
        className="h-full max-h-56 min-h-40 rounded-xl border border-border"
        fadeClassName="from-card rounded-b-xl"
      >
        <ul className="divide-y divide-border">
          {rows.map(row => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => onOpen(row)}
                title={row.hint}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted/50"
              >
                {/* The name gives up room before the detail does: the detail is the half of the label
                    that says which row this is, so truncating it away would leave two rows identical. */}
                <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
                  <span className="truncate font-medium">{row.name}</span>
                  {row.detail ? <span className="shrink-0 text-xs text-muted-foreground">{row.detail}</span> : null}
                </span>
                {row.badge}
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>
      </ScrollFade>
    );
  };

  return (
    <Card className="flex h-full flex-col p-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-base font-semibold text-foreground">
          <Icon className="size-4 shrink-0 text-muted-foreground" />
          {title}
        </p>
        {meta && !failed ? <span className="text-xs text-muted-foreground">{meta}</span> : null}
      </div>
      {body()}
    </Card>
  );
};
